import {
    type WithDialogProps,
    Text,
    type AppBottomSheetInjectedProps as BottomSheetExtraProps,
    withDialog,
    withAppBottomSheet,
    SupportMenuItem,
    cn,
} from '@kidslabs/mario-nextjs-react-ui-core';
import { type ComponentProps, useCallback, useMemo } from 'react';
import { useFetchSupportMenuItems } from '../../api/requests';
import { SupportMenuActionType, type SupportMenuItem as SupportMenuItemProps } from '../../types/front.graphql';
import { type SupportMenuProps } from './support-menu.types';
import styles from './support-menu.module.css';

const SupportMenuContent = ({
    location,
    bottomSheet,
    isMobile = true,
}: SupportMenuProps & BottomSheetExtraProps & WithDialogProps) => {
    const { supportMenuItems, loading } = useFetchSupportMenuItems(location);

    const contentProps: ComponentProps<typeof SupportMenuItem>['supportMenuItemContentProps'] = useMemo(() => {
        return {
            containerClassName: styles.supportMenuItemContent,
        };
    }, []);

    const handleOpenBottomSheet = useCallback(
        (item: SupportMenuItemProps) => {
            bottomSheet.inject(
                <Text>{item?.support_menu_action?.value ?? ''}</Text>,
                {
                    title: item?.menu_label ?? '',
                    titleProps: { variant: 'h1' },
                    closeIconProps: {
                        icon: 'blank',
                        disabled: true,
                        iconColor: 'transparent',
                    },
                },
                {
                    enableDynamicSizing: false,
                    scrollEnabled: false,
                    snapPoints: ['20%'],
                },
                undefined,
                {
                    pressBehavior: 'close',
                }
            );
            bottomSheet.show();
        },
        [bottomSheet]
    );

    const handleRequestCancel = useCallback(() => {
        return;
    }, []);

    const handlePressItem = useCallback(
        (item: SupportMenuItemProps) => {
            const action = item?.support_menu_action;

            switch (action?.type) {
                case SupportMenuActionType.Call:
                    if (!action?.value && !isMobile) {
                        handleOpenBottomSheet(item);
                    }
                    break;
                case SupportMenuActionType.RequestCancel:
                    handleRequestCancel();
                    break;
                case SupportMenuActionType.OpenBottomSheet:
                    handleOpenBottomSheet(item);
                    break;
                default:
                    break;
            }
        },
        [isMobile, handleOpenBottomSheet, handleRequestCancel]
    );

    const link = useCallback(
        (item: SupportMenuItemProps) =>
            item?.support_menu_action?.type === SupportMenuActionType.Navigate
                ? item?.support_menu_action.value
                : undefined,
        []
    );

    if (loading) {
        return null;
    }

    if (!supportMenuItems?.length) {
        return null;
    }

    return (
        <div className={styles.container}>
            {supportMenuItems?.map((item, index) => {
                const supportLink = link(item) ? `${link(item)}` : undefined;
                if (!item) {
                    return null;
                }

                return (
                    <SupportMenuItem
                        className={cn(styles.supportMenuItem, index === supportMenuItems.length - 1 && styles.lastItem)}
                        supportMenuItemContentProps={contentProps}
                        link={supportLink}
                        key={item?.id}
                        title={item?.menu_label ?? ''}
                        titleClassname={styles.title}
                        onPress={() => handlePressItem(item)}
                    />
                );
            })}
        </div>
    );
};

const SupportMenuInner = withDialog(withAppBottomSheet(SupportMenuContent));

export const SupportMenu = (props: SupportMenuProps) => {
    return <SupportMenuInner {...props} />;
};
